import { useState, useEffect, useRef, useCallback } from 'react';

// Debug flag - set to true to enable colored containers and console logging
const DEBUG_COLORS = false;

function PDFSidebar({

  pdfFile,

  currentPageIndex,

  totalPages,

  onFirstPage,

  onPreviousPage,

  onNextPage,

  onLastPage,

  filters,

  onFilterChange,

  onReset,

  onDownload,

  isProcessing,

  hasError,

  onOpenSettings,

  pageExceptions = {},

  currentPageNumber,

  hasPageException,

  onApplyToCurrentPage,

  onManageExceptions,

  onSavePreset,

  savedPresets = [],

  onApplyPreset,

  onDeleteException,

  onPreservePage,

  currentViewMode = 'preview', // 'preview' or 'contactSheet'

  selectedContactSheetPages = new Set(), // Selected pages in Contact Sheet mode

  onAutoOptimize, // Handler for auto-optimize feature

  onPageSettingModeChange, // Callback to notify parent of mode changes

  onForcePresetCreationChange, // Callback to notify parent of force preset creation flag

  previewFilters, // Current preview filters (if any)

  setPreviewFilters, // Callback to set preview filters

  setPreviewPages, // Callback to set preview pages

  onCancelPreview, // Callback to cancel preview

  // Lifted states and setters

  manualModeChange,

  setManualModeChange,

  modeWasAutoSelected,

  setModeWasAutoSelected,

  pageSettingMode,

  setPageSettingMode,

  bottomSheetOpen,

  setBottomSheetOpen,

  originalPageExceptions,

  setOriginalPageExceptions,

  previewedPresetId,

  setPreviewedPresetId,

  originalCustomFilters,

  setOriginalCustomFilters,

  checkUnsavedChanges,

  handleSetManualModeChange,

}) {
  const [showMoreOptions, setShowMoreOptions] = useState(false);









  const [bottomSheetMode, setBottomSheetMode] = useState(''); // Which adjustment panel to show: 'preserve', 'preset', 'custom', or 'auto-optimize'
  const [creatingNewPreset, setCreatingNewPreset] = useState(false); // Track if creating a new preset from preset slide-in
  const [newPresetName, setNewPresetName] = useState(''); // Name for new preset being created
  const moreOptionsRef = useRef(null);
  const previousPresetCount = useRef(savedPresets?.length || 0);

  // Notify parent of page setting mode changes
  useEffect(() => {
    console.log('🔔 PDFSidebar - Notifying parent of pageSettingMode change:', pageSettingMode);
    if (onPageSettingModeChange) {
      onPageSettingModeChange(pageSettingMode);
    }
  }, [pageSettingMode, onPageSettingModeChange]);

  // Notify parent of force preset creation flag changes
  useEffect(() => {
    if (onForcePresetCreationChange) {
      onForcePresetCreationChange(forcePresetCreation);
    }
  }, [forcePresetCreation, onForcePresetCreationChange]);

  // Clear forcePresetCreation flag when a new preset is successfully saved
  useEffect(() => {
    const currentCount = savedPresets?.length || 0;
    if (forcePresetCreation && currentCount > previousPresetCount.current) {
      // A new preset was added, so clear the creation flag to show the dropdown
      setForcePresetCreation(false);
    }
    previousPresetCount.current = currentCount;
  }, [savedPresets, forcePresetCreation]);



  // Check if any adjustments have been made (global or page-specific)
  const hasAnyAdjustments = () => {
    // Check if global filters differ from defaults
    if (hasGlobalAdjustment()) {
      return true;
    }

    // Check if any page has exceptions (custom settings or preserved)
    const hasPageExceptions = Object.keys(pageExceptions).some(pageNum => {
      const exception = pageExceptions[pageNum];
      return exception && (exception.enabled || exception.preserveOriginal);
    });

    return hasPageExceptions;
  };

  // Auto-detect common mode for selected pages
  useEffect(() => {
    console.log('🔄 Auto-detection useEffect triggered');
    console.log('   currentViewMode:', currentViewMode);
    console.log('   selectedContactSheetPages.size:', selectedContactSheetPages.size);
    console.log('   manualModeChange:', manualModeChange);

    // Skip auto-detection if user manually changed mode
    if (manualModeChange) {
      console.log('   ⏭️ Skipping - manual mode change');
      // Don't reset manualModeChange here - let it persist until page navigation
      return;
    }

    // Only auto-detect in Contact Sheet mode when pages are selected
    if (currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0) {
      const selectedPagesArray = Array.from(selectedContactSheetPages);

      console.log('🔍 Running auto-detection for', selectedPagesArray.length, 'pages');
      console.log('   pageExceptions:', pageExceptions);
      console.log('   First 3 pages:', selectedPagesArray.slice(0, 3));

      // Check if all selected pages share a common mode
      let allPreserved = true;
      let allAutoOptimized = true;
      let allCustom = true;
      let allPreset = true;

      selectedPagesArray.forEach(pageNum => {
        const exception = pageExceptions[pageNum];
        const isPreserved = exception?.preserveOriginal;
        const hasGlobalException = exception?.enabled && exception?.useGlobal;
        const isAutoOptimized = exception?.enabled && exception?.autoOptimized && !exception?.useGlobal;
        const hasPresetException = exception?.enabled && (exception?.presetId || exception?.label) && !exception?.useGlobal && !exception?.autoOptimized;
        const hasCustomSettings = exception?.enabled && !isPreserved && !exception?.useGlobal && !exception?.presetId && !exception?.label && !exception?.autoOptimized;

        if (!isPreserved) allPreserved = false;
        if (!isAutoOptimized) allAutoOptimized = false;
        if (!hasCustomSettings) allCustom = false;
        if (!hasPresetException) allPreset = false;
      });

      console.log('   Results:', { allPreserved, allAutoOptimized, allCustom, allPreset });

      // Set mode based on what's common (only if adjustments exist)
      if (allPreserved) {
        console.log('   ✅ Setting mode to: preserve (auto-selected)');
        setPageSettingMode('preserve');
        setModeWasAutoSelected(true);
      } else if (allAutoOptimized) {
        console.log('   ✅ Setting mode to: auto-optimize (auto-selected)');
        setPageSettingMode('auto-optimize');
        setModeWasAutoSelected(true);
      } else if (allPreset) {
        console.log('   ✅ Setting mode to: preset (auto-selected)');
        setPageSettingMode('preset');
        setModeWasAutoSelected(true);
      } else if (allCustom) {
        console.log('   ✅ Setting mode to: custom (auto-selected)');
        setPageSettingMode('custom');
        setModeWasAutoSelected(true);
      } else {
        console.log('   ✅ Setting mode to: empty (mixed/no adjustments)');
        // Mixed modes or no adjustments - no selection
        setPageSettingMode('');
        setModeWasAutoSelected(false);
      }
    } else if (currentViewMode === 'preview' && currentPageNumber) {
      // In preview mode, auto-detect current page mode
      const exception = pageExceptions[currentPageNumber];
      const isPreserved = exception?.preserveOriginal;
      const hasCustomSettings = exception?.enabled && !isPreserved;

      if (isPreserved) {
        setPageSettingMode('preserve');
        setModeWasAutoSelected(true);
      } else if (hasCustomSettings) {
        // Check if it's using global settings (check useGlobal flag first)
        if (exception?.autoOptimized) {
          // Auto-optimized page
          setPageSettingMode('auto-optimize');
          setModeWasAutoSelected(true);
        } else if (exception?.label) {
          // Has a label but not useGlobal or autoOptimized - it's a preset
          setPageSettingMode('preset');
          setModeWasAutoSelected(true);
        } else {
          setPageSettingMode('custom');
          setModeWasAutoSelected(true);
        }
      } else {
        // No exception - no auto-selection
        // Pages without exceptions should NOT auto-select global mode
        setPageSettingMode('');
        setModeWasAutoSelected(false);
      }
    } else {
      // No selection - clear mode
      setPageSettingMode('');
      setModeWasAutoSelected(false);
    }
  }, [currentViewMode, selectedContactSheetPages, pageExceptions, currentPageNumber, manualModeChange]);

  // Helper function to get preset state for selected pages
  const getPresetState = () => {
    if (currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0) {
      // Multiple pages selected
      const selectedPagesArray = Array.from(selectedContactSheetPages);
      const presetLabels = new Set();

      selectedPagesArray.forEach(pageNum => {
        const exception = pageExceptions[pageNum];
        if (exception?.enabled && exception?.label && !exception?.useGlobal) {
          presetLabels.add(exception.label);
        }
      });

      if (presetLabels.size === 0) {
        return { hasPreset: false, presetName: null, isMixed: false };
      } else if (presetLabels.size === 1) {
        return { hasPreset: true, presetName: Array.from(presetLabels)[0], isMixed: false };
      } else {
        return { hasPreset: true, presetName: null, isMixed: true };
      }
    } else {
      // Single page (preview mode or no selection)
      const exception = pageExceptions[currentPageNumber];
      if (exception?.enabled && exception?.label && !exception?.useGlobal) {
        return { hasPreset: true, presetName: exception.label, isMixed: false };
      }
      return { hasPreset: false, presetName: null, isMixed: false };
    }
  };

  const presetState = getPresetState();

  // Determine current page state
  const currentPageException = pageExceptions[currentPageNumber];
  const isPreserved = currentPageException?.preserveOriginal;
  const hasCustomSettings = currentPageException?.enabled && !isPreserved;
  const isUsingGlobal = !currentPageException?.enabled && !isPreserved;

  // Update mode based on current page state (only in Preview mode)
  // In Contact Sheet mode, auto-detection handles mode selection
  useEffect(() => {
    // IMPORTANT: Only run this in Preview mode
    // Contact Sheet mode has its own auto-detection in the effect above
    if (currentViewMode !== 'preview') {
      return;
    }

    // Skip auto-detection if user manually cleared or changed mode
    // IMPORTANT: Don't reset manualModeChange here - let it persist until next page navigation
    if (manualModeChange) {
      console.log('   ⏭️ Preview mode - Skipping auto-detection due to manual mode change');
      return;
    }

    if (currentPageNumber) {
      if (isPreserved) {
        setPageSettingMode('preserve');
        setModeWasAutoSelected(true);
      } else if (hasCustomSettings) {
        // Check if it's using global settings (check useGlobal flag first)
        if (currentPageException?.useGlobal) {
            setModeWasAutoSelected(true);
        } else if (currentPageException?.label) {
          // Has a label but not useGlobal - it's a preset
          setPageSettingMode('preset');
          setModeWasAutoSelected(true);
        } else {
          setPageSettingMode('custom');
          setModeWasAutoSelected(true);
        }

      } else {
        // No adjustments at all - leave mode cleared
        setPageSettingMode('');
        setModeWasAutoSelected(false);
      }
    }
  }, [currentViewMode, currentPageNumber, isPreserved, hasCustomSettings, manualModeChange]);

  // Reset manualModeChange flag when page selection changes (but not when mode changes)
  useEffect(() => {
    if (currentViewMode === 'preview' && currentPageNumber) {
      // In preview mode, reset when current page changes
      setManualModeChange(false);
    } else if (currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0) {
      // In contact sheet mode, reset when page selection changes
      setManualModeChange(false);
    }
  }, [currentViewMode, currentPageNumber, selectedContactSheetPages]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreOptionsRef.current && !moreOptionsRef.current.contains(event.target)) {
        setShowMoreOptions(false);
      }
    };

    if (showMoreOptions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMoreOptions]);

  const hasException = (pageNumber) => {
    return pageExceptions[pageNumber] && pageExceptions[pageNumber].enabled;
  };

  // Wrapper for filter changes that updates page exception when in custom mode
  const handleFilterChange = (filterName, value) => {
    // Always update the global filter first
    onFilterChange(filterName, value);

    // If in custom mode, also update the page exception with the new filters
    if (pageSettingMode === 'custom' && onApplyToCurrentPage) {
      // Use setTimeout to ensure the filter state has been updated
      setTimeout(() => {
        const updatedFilters = { ...filters, [filterName]: value };

        // In Contact Sheet mode with selected pages, apply to all selected
        // In Preview mode, apply to current page only
        if (currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0) {
          // Apply to all selected pages
          Array.from(selectedContactSheetPages).forEach(pageNum => {
            onApplyToCurrentPage(pageNum, updatedFilters);
          });
        } else if (currentPageNumber) {
          // Apply to current page only (Preview mode or no selection)
          onApplyToCurrentPage(currentPageNumber, updatedFilters);
        }
      }, 0);
    }
  };

  return (
    // Sidebar Main Container
    <div
      className={`w-full rounded-lg shadow-lg p-1 md:p-3 pb-6 md:pb-8 flex flex-col md:h-full ${DEBUG_COLORS ? 'bg-green-100' : 'bg-white dark:bg-gray-800'}`}
      {...(DEBUG_COLORS && {
        onClick: () => console.log('🟢 GREEN-100: Sidebar Main Container'),
        onTouchStart: () => console.log('🟢 GREEN-100: Sidebar Main Container')
      })}
    >
      {/* Document Details Section - Collapsible - Hidden on mobile (accessible via overlay button) */}
      <div className="hidden md:block bg-white dark:bg-gray-700 rounded-lg p-3 shadow-sm">
        <button
          onClick={() => setShowDocumentDetails(!showDocumentDetails)}
          className="w-full flex items-center justify-between text-base font-bold text-gray-800 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          <span>Document Details</span>
          <span className="text-xl">{showDocumentDetails ? '▼' : '▶'}</span>
        </button>
        {showDocumentDetails && (
          <div className="bg-gray-50 dark:bg-gray-600 rounded-lg p-3 space-y-1.5 mt-2">
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase mb-1">Filename</p>
            <p className="text-sm text-gray-800 dark:text-gray-100 break-words font-medium">{pdfFile.name}</p>
          </div>
          <div className="flex gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase mb-1">Size</p>
              <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">
                {(pdfFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            {totalPages > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase mb-1">Pages</p>
                <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">{totalPages}</p>
              </div>
            )}
            {/* Status */}
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase mb-1">Status</p>
              {isProcessing ? (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-100 dark:bg-indigo-900/50">
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent"></div>
                  <span className="text-xs text-indigo-700 dark:text-indigo-300 font-semibold">Processing</span>
                </div>
              ) : hasError ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-100 dark:bg-red-900/50 text-xs text-red-700 dark:text-red-300 font-semibold">
                  ⚠️ Error
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-green-100 dark:bg-green-900/50 text-xs text-green-700 dark:text-green-300 font-semibold">
                  Ready
                </span>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Main Content Area - Scrollable with flex-1 to push Settings to bottom */}
      <div className="flex-1 md:overflow-y-auto space-y-1 md:space-y-3 mt-1 md:mt-3">
      {/* Page Navigation Section - Only show in preview mode on desktop (mobile has footer navigation) */}
      {currentViewMode === 'preview' && (
        <div className="hidden md:block bg-white dark:bg-gray-700 rounded-lg p-3 shadow-sm">
          {/* Compact header with inline page indicator */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-gray-800 dark:text-gray-100">Page Navigation</h3>
            <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
              Page {currentPageIndex + 1} of {totalPages}
            </p>
          </div>

          {/* Navigation Buttons - Single row with 4 columns */}
          <div className="hidden md:block">
            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={onFirstPage}
                disabled={currentPageIndex === 0}
                className="px-2 md:px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                title="First page"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6 1.41-1.41zM6 6h2v12H6V6z" />
                </svg>
              </button>
              <button
                onClick={onPreviousPage}
                disabled={currentPageIndex === 0}
                className="px-2 md:px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                title="Previous page"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z" />
                </svg>
              </button>
              <button
                onClick={onNextPage}
                disabled={currentPageIndex === totalPages - 1}
                className="px-2 md:px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                title="Next page"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" />
                </svg>
              </button>
              <button
                onClick={onLastPage}
                disabled={currentPageIndex === totalPages - 1}
                className="px-2 md:px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                title="Last page"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6-1.41 1.41zM16 6h2v12h-2V6z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Settings Section - Only show when pages are selected AND currentPageNumber exists */}
      {currentPageNumber && ((currentViewMode === 'preview') ||
        (currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0)) && (
      <div
        className={`rounded-none md:rounded-lg p-0 md:p-3 shadow-none md:shadow-sm ${DEBUG_COLORS ? 'bg-green-200' : 'bg-transparent md:bg-white dark:bg-transparent md:dark:bg-gray-700'}`}
        {...(DEBUG_COLORS && {
          onClick: (e) => { e.stopPropagation(); console.log('🟢 GREEN-200: Page Adjustments Section'); },
          onTouchStart: (e) => { e.stopPropagation(); console.log('🟢 GREEN-200: Page Adjustments Section'); }
        })}
      >
        <h3 className="hidden md:block text-base font-bold text-gray-800 dark:text-gray-100 mb-2">
          {currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
            ? 'Multiple Page Adjustments'
            : 'Page Settings'}
        </h3>

        {currentPageNumber && (
          <>
            {/* Mode Selection */}
            <div className="mb-1 md:mb-4">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 md:mb-2">
                <span className="md:hidden">Page Adjustments</span>
                <span className="hidden md:inline uppercase">Select page mode:</span>
              </p>

              {(() => {
                console.log('🎨 Rendering buttons - pageSettingMode:', pageSettingMode, 'modeWasAutoSelected:', modeWasAutoSelected);
                return null;
              })()}

              {/* Grid layout: 5 columns on mobile (icons only), 1 column on desktop (full buttons) */}
              <div className="grid grid-cols-5 md:grid-cols-1 gap-1 md:gap-2">

                  {/* Use Preset Adjustment */}
                  <div
                    className={`rounded-lg border-2 transition-all ${
                      pageSettingMode === 'preset'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 dark:border-purple-600'
                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-500'
                    }`}
                  >
                    <button
                      onClick={() => {
                        // Check for unsaved changes
                        if (!checkUnsavedChanges('preset')) return;

                        // Ensure preview is cleared when entering Preset mode
                        if (setPreviewFilters && setPreviewPages) {
                          setPreviewFilters(null);
                          setPreviewPages(new Set());
                        }

                        // Mobile: Open bottom sheet to show preset options, or toggle off if already active
                        const isMobile = window.innerWidth < 768;
                        if (isMobile) {
                          // If already in preset mode, toggle off (remove preset adjustments)
                          if (pageSettingMode === 'preset') {
                            setManualModeChange(false);
                            setPageSettingMode('');
                            setModeWasAutoSelected(false);
                            setForcePresetCreation(false);

                            // Remove preset exceptions from these pages (revert to default: global settings)
                            if (onDeleteException) {
                              const pagesToClear = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                ? Array.from(selectedContactSheetPages)
                                : [currentPageNumber];

                              pagesToClear.forEach(pageNum => {
                                onDeleteException(pageNum);
                              });
                            }
                            return;
                          }

                          // Not in preset mode, so open bottom sheet to select/apply a preset
                          // Store original page exceptions for selected pages (for cancel functionality)
                          const selectedPages = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                            ? Array.from(selectedContactSheetPages)
                            : [currentPageNumber];

                          const originalExceptions = {};
                          selectedPages.forEach(pageNum => {
                            if (pageExceptions[pageNum]) {
                              originalExceptions[pageNum] = { ...pageExceptions[pageNum] };
                            }
                          });
                          setOriginalPageExceptions(originalExceptions);

                          // Open bottom sheet with preset options
                          // If no presets exist, force preset creation workflow
                          if (!savedPresets || savedPresets.length === 0) {
                            setForcePresetCreation(true);
                          }
                          setBottomSheetMode('preset');
                          setBottomSheetOpen(true);
                          return;
                        }

                        // Desktop: If already selected, toggle off
                        if (pageSettingMode === 'preset') {
                          // Clear manual flag to allow auto-detection to run after deletion
                          setManualModeChange(false);
                          setPageSettingMode('');
                          setModeWasAutoSelected(false);
                          setForcePresetCreation(false);

                          // Remove preset exceptions from these pages (revert to default: global settings)
                          if (onDeleteException) {
                            const pagesToClear = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                              ? Array.from(selectedContactSheetPages)
                              : [currentPageNumber];

                            pagesToClear.forEach(pageNum => {
                              onDeleteException(pageNum);
                            });
                          }
                        } else {
                          // Not selected, so select it
                          setManualModeChange(true);
                          setModeWasAutoSelected(false);
                          setPageSettingMode('preset');
                          // If no presets exist, force preset creation workflow
                          if (!savedPresets || savedPresets.length === 0) {
                            setForcePresetCreation(true);
                          }
                        }
                      }}
                      className="w-full h-full md:h-auto px-2 py-2 md:px-3 md:py-2.5 flex flex-col md:flex-row items-center justify-center md:text-left"
                    >
                      {/* Mobile: Icon + label, Desktop: Full layout */}
                      <div className="flex flex-col items-center gap-0.5 md:hidden">
                        <svg className="w-5 h-5 flex-shrink-0 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        <span className="text-[10px] font-medium text-gray-700 dark:text-gray-200">Presets</span>
                      </div>
                      <div className="hidden md:flex md:flex-row md:items-center gap-2 w-full">
                        <svg className="w-5 h-5 flex-shrink-0 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">
                            Use Preset Adjustment
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            {savedPresets && savedPresets.length > 0
                              ? `${savedPresets.length} preset${savedPresets.length !== 1 ? 's' : ''} available`
                              : 'No presets saved yet'
                            }
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-0.5">
                          {pageSettingMode === 'preset' && <span className="text-purple-600 dark:text-purple-400 text-base">✓</span>}
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Custom Adjustment */}
                  <button
                    onClick={() => {
                      // Check for unsaved changes (pass 'custom' to allow re-entering Custom mode)
                      if (!checkUnsavedChanges('custom')) return;

                      // Ensure preview is cleared when entering Custom mode
                      if (setPreviewFilters && setPreviewPages) {
                        setPreviewFilters(null);
                        setPreviewPages(new Set());
                      }

                      // Mobile: If already selected, toggle off
                      const isMobile = window.innerWidth < 768;
                      if (isMobile) {
                        if (pageSettingMode === 'custom') {
                          // Clear manual flag to allow auto-detection to run after deletion
                          setManualModeChange(false);
                          setPageSettingMode('');
                          setModeWasAutoSelected(false);
                          // Remove custom adjustments from pages
                          if (onDeleteException) {
                            const pagesToClear = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                              ? Array.from(selectedContactSheetPages)
                              : [currentPageNumber];

                            pagesToClear.forEach(pageNum => {
                              const pageException = pageExceptions[pageNum];
                              if (pageException?.enabled) {
                                onDeleteException(pageNum);
                              }
                            });

                            // Page will now use default (global) settings
                            // Do NOT preserve - that would prevent global adjustments from applying
                          }
                        } else {
                          // Not selected, so select it
                          setManualModeChange(true);
                          setModeWasAutoSelected(false);
                          setPageSettingMode('custom');
                          // Store original filters for comparison when exiting Custom mode
                          setOriginalCustomFilters({ ...filters });

                          // Initialize preview with page's existing custom settings if available, otherwise use current filters
                          if (setPreviewFilters && setPreviewPages) {
                            // Check if page already has custom adjustment
                            const exception = pageExceptions[currentPageNumber];
                            const hasCustom = exception && exception.enabled && exception.label === '';

                            // Use page's custom settings if they exist, otherwise use global filters
                            const initialFilters = hasCustom ? { ...exception.settings } : { ...filters };
                            console.log('[Custom Adjustment] Initializing preview filters:', {
                              hasCustom,
                              initialFilters
                            });
                            setPreviewFilters(initialFilters);

                            const pagesToPreview = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                              ? selectedContactSheetPages
                              : new Set([currentPageNumber]);
                            setPreviewPages(pagesToPreview);
                          }

                          setBottomSheetMode('custom');
                          setBottomSheetOpen(true);
                        }
                        return;
                      }

                      // Desktop: Always open the panel (don't toggle off)
                      // Clicking Custom Adjustment should always open the panel to edit/create custom settings
                      console.log('🔧 Custom Adjustment clicked - entering/reopening custom mode');
                      console.log('   Current view mode:', currentViewMode);
                      console.log('   Selected pages:', Array.from(selectedContactSheetPages));
                      console.log('   Selected pages size:', selectedContactSheetPages.size);
                      console.log('   Current page number:', currentPageNumber);
                      console.log('   Filters:', filters);

                      setManualModeChange(true);
                      setModeWasAutoSelected(false);
                      setPageSettingMode('custom');
                      // Store original filters for comparison when exiting Custom mode
                      setOriginalCustomFilters({ ...filters });

                      // Initialize preview with page's existing custom settings if available, otherwise use current filters
                      if (setPreviewFilters && setPreviewPages) {
                        // Check if page already has custom adjustment
                        const exception = pageExceptions[currentPageNumber];
                        const hasCustom = exception && exception.enabled && exception.label === '';

                        // Use page's custom settings if they exist, otherwise use global filters
                        const initialFilters = hasCustom ? { ...exception.settings } : { ...filters };
                        console.log('[Custom Adjustment] Initializing preview filters:', {
                          hasCustom,
                          initialFilters
                        });
                        setPreviewFilters(initialFilters);

                        const pagesToPreview = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                          ? selectedContactSheetPages
                          : new Set([currentPageNumber]);
                        console.log('   📋 Pages to preview:', Array.from(pagesToPreview));
                        setPreviewPages(pagesToPreview);
                        console.log('   ✅ Custom preview mode initialized');
                      }
                    }}
                    className={`relative h-full px-2 py-2 md:px-3 md:py-2.5 rounded-lg border-2 transition-all flex flex-col md:flex-row items-center justify-center md:text-left ${
                      (() => {
                        // Highlight button if:
                        // 1. Panel is currently open in custom mode, OR
                        // 2. Page has a custom exception (even if panel is closed)
                        const isCustomModeActive = pageSettingMode === 'custom';

                        // Check if current page(s) have custom exceptions (not Preserve or Preset)
                        const pagesToCheck = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                          ? Array.from(selectedContactSheetPages)
                          : [currentPageNumber];

                        const hasCustomException = pagesToCheck.some(pageNum => {
                          const exception = pageExceptions[pageNum];
                          // Custom exception: enabled, empty label (not a preset), and NOT preserveOriginal
                          return exception && exception.enabled && exception.label === '' && !exception.preserveOriginal && !exception.useGlobal;
                        });

                        return (isCustomModeActive || hasCustomException)
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-600'
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-500';
                      })()
                    }`}
                  >
                    {/* Mobile: Icon + label, Desktop: Full layout */}
                    <div className="flex flex-col items-center gap-0.5 md:hidden">
                      <svg className="w-5 h-5 flex-shrink-0 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      <span className="text-[10px] font-medium text-gray-700 dark:text-gray-200">Custom</span>
                    </div>
                    <div className="hidden md:flex md:flex-row md:items-center gap-2 w-full">
                      <svg className="w-5 h-5 flex-shrink-0 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">
                          Custom Adjustment
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                          {currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                            ? 'Fine-tune settings for selected pages'
                            : `Fine-tune settings for page ${currentPageNumber}`}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        {(() => {
                          // Show checkmark if panel is open in custom mode OR page has custom exception
                          const isCustomModeActive = pageSettingMode === 'custom';

                          const pagesToCheck = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                            ? Array.from(selectedContactSheetPages)
                            : [currentPageNumber];

                          const hasCustomException = pagesToCheck.some(pageNum => {
                            const exception = pageExceptions[pageNum];
                            // Custom exception: enabled, empty label (not a preset), and NOT preserveOriginal
                            return exception && exception.enabled && exception.label === '' && !exception.preserveOriginal && !exception.useGlobal;
                          });

                          return (isCustomModeActive || hasCustomException) && (
                            <span className="text-indigo-600 dark:text-indigo-400 text-base">✓</span>
                          );
                        })()}
                      </div>
                    </div>
                  </button>

                  {/* Auto-Optimize - Works for both single page and multiple pages */}
                  <button
                    onClick={() => {
                      // Check for unsaved changes
                      if (!checkUnsavedChanges('auto-optimize')) return;

                      // Ensure preview is cleared when entering Auto-Optimize mode
                      if (setPreviewFilters && setPreviewPages) {
                        setPreviewFilters(null);
                        setPreviewPages(new Set());
                      }

                      // Mobile: Open bottom sheet
                      const isMobile = window.innerWidth < 768;
                      if (isMobile) {
                        setBottomSheetMode('auto-optimize');
                        setBottomSheetOpen(true);
                        return;
                      }

                      // Desktop: Trigger auto-optimization analysis
                      // This will analyze pages and suggest best settings to minimize ink
                      // Works in both Preview mode (single page) and Contact Sheet (multiple pages)
                      if (onAutoOptimize) {
                        const pagesToAnalyze = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                          ? Array.from(selectedContactSheetPages)
                          : [currentPageNumber];
                        onAutoOptimize(pagesToAnalyze);
                      }
                    }}
                    className={`relative h-full px-2 py-2 md:px-3 md:py-2.5 rounded-lg border-2 transition-all flex flex-col md:flex-row items-center justify-center md:text-left ${
                      pageSettingMode === 'auto-optimize'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 dark:border-purple-600'
                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-500'
                    }`}
                  >
                    {/* Mobile: Icon + label, Desktop: Full layout */}
                    <div className="flex flex-col items-center gap-0.5 md:hidden">
                      <svg className="w-5 h-5 flex-shrink-0 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      <span className="text-[10px] font-medium text-gray-700 dark:text-gray-200 text-center leading-tight">Auto<br/>Optimize</span>
                    </div>
                    <div className="hidden md:flex md:flex-row md:items-center gap-2 w-full">
                      <svg className="w-5 h-5 flex-shrink-0 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight">
                          Auto-Optimize for Ink Savings
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                          {currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                            ? `Analyze ${selectedContactSheetPages.size} page${selectedContactSheetPages.size !== 1 ? 's' : ''} for best settings`
                            : 'Analyze current page for best settings'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        {pageSettingMode === 'auto-optimize' && <span className="text-purple-600 dark:text-purple-400 text-base">✓</span>}
                      </div>
                    </div>
                  </button>
              </div>
            </div>


          </>
        )}

      </div>
      )}

      {/* Action Buttons - Mobile only (all buttons inside are md:hidden) */}
      <div
        className={`md:hidden rounded-lg p-1 shadow-sm space-y-1.5 ${DEBUG_COLORS ? 'bg-green-300' : 'bg-white dark:bg-gray-700'}`}
        {...(DEBUG_COLORS && {
          onClick: (e) => { e.stopPropagation(); console.log('🟢 GREEN-300: Action Buttons (Settings, Upload)'); },
          onTouchStart: (e) => { e.stopPropagation(); console.log('🟢 GREEN-300: Action Buttons (Settings, Upload)'); }
        })}
      >
        {/* Action Buttons - All on same row on mobile, stacked on desktop */}
        <div className="grid grid-cols-5 md:grid-cols-1 gap-1.5 md:gap-0 md:space-y-1.5">
          {/* Download button - takes 3 columns on mobile (most space), hidden on desktop */}
          {hasAnyAdjustments() && (
            <button
              onClick={onDownload}
              disabled={isProcessing || hasError || !hasAnyAdjustments()}
              className="md:hidden col-span-3 px-2 py-2.5 bg-indigo-600 dark:bg-indigo-700 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-800 transition-colors text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Download</span>
            </button>
          )}

          {/* Settings button - takes 1 column on mobile, hidden on desktop */}
          <button
            onClick={onOpenSettings}
            className={`md:hidden ${hasAnyAdjustments() ? 'col-span-1' : 'col-span-2'} px-2 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-xs font-medium flex items-center justify-center gap-1`}
          >
            <span>⚙️</span> <span className="text-xs">Settings</span>
          </button>

          {/* Upload button - takes 1 column on mobile (or 3 if no download), hidden on desktop */}
          <button
            onClick={() => {
              // Check for unsaved changes
              if (!checkUnsavedChanges()) return;
              onReset();
            }}
            className={`md:hidden ${hasAnyAdjustments() ? 'col-span-1' : 'col-span-3'} px-2 py-2.5 bg-gray-500 dark:bg-gray-600 text-white rounded-lg hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors text-xs font-medium`}
          >
            <span>Upload</span>
          </button>
        </div>
      </div>
      </div>
      {/* End Main Content Area */}

      {/* Desktop Download Button - Positioned at bottom, hidden on mobile */}
      {hasAnyAdjustments() && (
        <div className="hidden md:block mt-auto pt-3">
          <button
            onClick={onDownload}
            disabled={isProcessing || hasError || !hasAnyAdjustments()}
            className="w-full px-6 py-3 bg-indigo-600 dark:bg-indigo-700 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-800 transition-colors text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Download Optimized PDF
          </button>
        </div>
      )}

      {/* Desktop Upload New File Button - Positioned at bottom, hidden on mobile */}
      <div className={`hidden md:block pt-3 ${!hasAnyAdjustments() ? 'mt-auto' : ''}`}>
        <button
          onClick={() => {
            // Check for unsaved changes
            if (!checkUnsavedChanges()) return;
            onReset();
          }}
          className="w-full px-6 py-3 bg-gray-500 dark:bg-gray-600 text-white rounded-lg hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors text-base font-medium flex items-center justify-center gap-2"
        >
          <span>📄</span> <span>Upload New File</span>
        </button>
      </div>

      {/* Desktop Settings Button - Positioned at bottom, hidden on mobile */}
      <div className="hidden md:block pt-3">
        {/* Separator bar */}
        <div className="border-t border-gray-300 dark:border-gray-600 mb-3"></div>

        <button
          onClick={onOpenSettings}
          className="w-full px-6 py-3 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-base font-medium flex items-center justify-center gap-2"
        >
          <span>⚙️</span> <span>Settings...</span>
        </button>
      </div>

      {/* Mobile Modal for Adjustment Controls */}
      {bottomSheetOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 pointer-events-auto"
            onClick={() => {
              setBottomSheetOpen(false);
              setCreatingNewPreset(false);
              setNewPresetName('');
            }}
          />

          {/* Modal Panel */}
          <div
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-h-[70vh] w-full max-w-lg flex flex-col pointer-events-auto"
            style={{
              animation: 'fadeIn 0.2s ease-out'
            }}
          >

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                {bottomSheetMode === 'preserve' && 'Preserve Original'}

                {bottomSheetMode === 'preset' && !creatingNewPreset && 'Apply Preset'}
                {bottomSheetMode === 'preset' && creatingNewPreset && 'Create New Preset'}
                {bottomSheetMode === 'custom' && 'Custom Adjustments'}
                {bottomSheetMode === 'auto-optimize' && 'Auto-Optimize for Ink Savings'}
              </h3>
              <button
                onClick={() => {


                  // For Preset mode, clear preview on close
                  if (bottomSheetMode === 'preset') {
                    if (setPreviewFilters && setPreviewPages) {
                      setPreviewFilters(null);
                      setPreviewPages(new Set());
                    }

                    // Reset mode to allow auto-detection to run
                    setManualModeChange(false);
                    setPageSettingMode('');
                  }

                  // For Custom mode, check if selected pages have custom adjustments
                  if (bottomSheetMode === 'custom') {
                    const selectedPages = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                      ? Array.from(selectedContactSheetPages)
                      : [currentPageNumber];

                    const hasCustomAdjustments = selectedPages.some(pageNum => {
                      const exception = pageExceptions[pageNum];
                      return exception && exception.enabled && exception.label === '';
                    });

                    // Only clear mode if selected pages don't have custom adjustments
                    if (!hasCustomAdjustments) {
                      setManualModeChange(false);
                      setPageSettingMode('');
                    }
                    // Otherwise keep mode as 'custom' to keep button highlighted
                  }

                  setBottomSheetOpen(false);
                  setCreatingNewPreset(false);
                  setNewPresetName('');
                  setOriginalPageExceptions(null);
                  setPreviewedPresetId(null);
                }}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content - scrollable */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {bottomSheetMode === 'preserve' && (
                <div className="space-y-4">
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                      ? `Keep ${selectedContactSheetPages.size} selected page${selectedContactSheetPages.size !== 1 ? 's' : ''} unchanged in the final PDF.`
                      : 'Keep this page unchanged in the final PDF.'}
                  </p>
                  <button
                    onClick={() => {
                      // If already selected, toggle off
                      if (pageSettingMode === 'preserve') {
                        // Clear manual flag to allow auto-detection to run after deletion
                        setManualModeChange(false);
                        setPageSettingMode('');
                        setModeWasAutoSelected(false);
                        // Delete exceptions
                        if (onDeleteException) {
                          const pagesToClear = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                            ? Array.from(selectedContactSheetPages)
                            : [currentPageNumber];

                          pagesToClear.forEach(pageNum => {
                            onDeleteException(pageNum);
                          });
                        }
                      } else {
                        // Not selected, so select it
                        setManualModeChange(true);
                        setModeWasAutoSelected(false);
                        setPageSettingMode('preserve');
                        if (onPreservePage) {
                          const pagesToPreserve = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                            ? Array.from(selectedContactSheetPages)
                            : [currentPageNumber];
                          onPreservePage(pagesToPreserve);
                        }
                      }
                      setBottomSheetOpen(false);
                    }}
                    className="w-full py-3 bg-amber-600 dark:bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-700 dark:hover:bg-amber-600 transition-colors"
                  >
                    {pageSettingMode === 'preserve' && !modeWasAutoSelected ? 'Remove Preserve' : 'Preserve Original'}
                  </button>
                </div>
              )}



              {bottomSheetMode === 'preset' && !creatingNewPreset && (
                <div className="space-y-4">
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                    {savedPresets && savedPresets.length > 0
                      ? 'Select a preset to preview:'
                      : 'No presets saved yet. Create a new preset below.'}
                  </p>
                  {savedPresets && savedPresets.length > 0 && (
                    <div className={`${savedPresets.length > 2 ? 'max-h-60 overflow-y-auto space-y-1.5 pr-1' : 'space-y-2'}`}>
                      {savedPresets.map((preset) => (
                        <div key={preset.id} className="space-y-1.5">
                          <button
                            onClick={() => {
                              // Preview the preset using preview system
                              setManualModeChange(true);
                              setModeWasAutoSelected(false);
                              setPageSettingMode('preset');
                              setPreviewedPresetId(preset.id); // Track which preset is being previewed

                              // Use preview system instead of applying directly
                              if (setPreviewFilters && setPreviewPages) {
                                console.log('[Mobile Preset Preview] Setting preview filters:', preset.settings);
                                setPreviewFilters(preset.settings);

                                // Set preview pages to selected pages
                                const pagesToPreview = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                  ? selectedContactSheetPages
                                  : new Set([currentPageNumber]);
                                console.log('[Mobile Preset Preview] Setting preview pages:', Array.from(pagesToPreview));
                                setPreviewPages(pagesToPreview);
                              }
                              // Don't close - let user preview and then click Apply or Cancel
                            }}
                            className={`w-full rounded-lg font-semibold transition-colors text-left ${
                              previewedPresetId === preset.id
                                ? 'bg-purple-700 dark:bg-purple-600 text-white border-2 border-purple-300 dark:border-purple-400'
                                : 'bg-purple-600 dark:bg-purple-500 text-white hover:bg-purple-700 dark:hover:bg-purple-600 border-2 border-transparent'
                            } ${
                              savedPresets.length > 2 ? 'py-2 px-3 text-sm' : 'py-3 px-4'
                            }`}
                          >
                            {preset.name}{previewedPresetId === preset.id && ' ✓'}
                          </button>

                          {/* Show preset values when selected */}
                          {previewedPresetId === preset.id && (
                            <div className="px-3 py-1.5 bg-purple-100 dark:bg-purple-800/40 rounded border border-purple-200 dark:border-purple-600">
                              <div className="flex flex-wrap gap-1.5 text-xs">
                                {preset.settings.invert && (
                                  <span className="px-1.5 py-0.5 bg-purple-200 dark:bg-purple-700 text-purple-900 dark:text-purple-100 rounded font-medium">
                                    Invert
                                  </span>
                                )}
                                {preset.settings.grayscale && (
                                  <span className="px-1.5 py-0.5 bg-purple-200 dark:bg-purple-700 text-purple-900 dark:text-purple-100 rounded font-medium">
                                    Grayscale
                                  </span>
                                )}
                                {preset.settings.removeBackground && (
                                  <span className="px-1.5 py-0.5 bg-purple-200 dark:bg-purple-700 text-purple-900 dark:text-purple-100 rounded font-medium">
                                    Remove BG
                                  </span>
                                )}
                                {preset.settings.brightness !== 100 && (
                                  <span className="px-1.5 py-0.5 bg-purple-200 dark:bg-purple-700 text-purple-900 dark:text-purple-100 rounded font-medium">
                                    Brightness: {preset.settings.brightness}%
                                  </span>
                                )}
                                {preset.settings.contrast !== 100 && (
                                  <span className="px-1.5 py-0.5 bg-purple-200 dark:bg-purple-700 text-purple-900 dark:text-purple-100 rounded font-medium">
                                    Contrast: {preset.settings.contrast}%
                                  </span>
                                )}
                                {preset.settings.bgThreshold !== 240 && (
                                  <span className="px-1.5 py-0.5 bg-purple-200 dark:bg-purple-700 text-purple-900 dark:text-purple-100 rounded font-medium">
                                    BG Threshold: {preset.settings.bgThreshold}
                                  </span>
                                )}
                                {preset.settings.bgSoftenEdges && (
                                  <span className="px-1.5 py-0.5 bg-purple-200 dark:bg-purple-700 text-purple-900 dark:text-purple-100 rounded font-medium">
                                    Soften Edges
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons - Smart conditional logic similar to Global */}
                  {(() => {
                    // Calculate button visibility logic
                    const selectedPages = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                      ? Array.from(selectedContactSheetPages)
                      : [currentPageNumber];

                    const allPagesSelected = totalPages && selectedPages.length === totalPages;

                    // Get all pages that have ANY preset
                    const pagesWithPreset = Object.keys(pageExceptions).filter(pageNum =>
                      pageExceptions[pageNum]?.enabled && pageExceptions[pageNum]?.presetId
                    ).map(Number);

                    // Check if all pages that have a preset are in the current selection
                    const selectedPagesSet = new Set(selectedPages);
                    const allPagesWithPresetAreSelected = pagesWithPreset.every(pageNum =>
                      selectedPagesSet.has(pageNum)
                    ) && pagesWithPreset.length > 0;

                    // Check if there were presets BEFORE opening the bottom sheet
                    // (only show remove buttons if there was something to remove originally)
                    // Check for presetId (new format) OR label matching a saved preset (old format for backwards compatibility)
                    const hadPresetsOriginally = originalPageExceptions &&
                      Object.values(originalPageExceptions).some(exc => {
                        if (exc?.presetId) return true; // New format with presetId
                        // Old format: check if label matches any saved preset
                        if (exc?.label && exc?.enabled) {
                          return savedPresets.some(preset => preset.name === exc.label);
                        }
                        return false;
                      });

                    // Determine if remove buttons are redundant:
                    // If all pages with preset are selected, then "Remove from Selected" and "Remove from All" are the same
                    const removeButtonsAreRedundant = allPagesWithPresetAreSelected;

                    // Should we show remove buttons at all?
                    // Only if there were presets originally (not just a preview of a new preset)
                    const showRemoveButtons = hadPresetsOriginally;

                    return (
                      <div className="space-y-3">
                        {/* If there are NO remove buttons and a preset is previewed: show Cancel, Apply, Create New horizontally */}
                        {!showRemoveButtons && previewedPresetId ? (
                          <div className="flex gap-2">
                            {/* Cancel Button */}
                            <button
                              onClick={() => {
                                // Clear preview filters
                                if (setPreviewFilters && setPreviewPages) {
                                  setPreviewFilters(null);
                                  setPreviewPages(new Set());
                                }

                                // Reset mode to allow auto-detection to run
                                setManualModeChange(false);
                                setPageSettingMode('');
                                setBottomSheetOpen(false);
                                setOriginalPageExceptions(null);
                                setPreviewedPresetId(null);
                              }}
                              className="flex-1 py-2 bg-gray-500 dark:bg-gray-600 text-white rounded-lg text-xs font-semibold hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
                            >
                              Cancel
                            </button>

                            {/* Apply Button */}
                            <button
                              onClick={() => {
                                // Apply the preset to the selected pages
                                if (previewedPresetId && onApplyPreset) {
                                  const pagesToApply = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                    ? Array.from(selectedContactSheetPages)
                                    : [currentPageNumber];
                                  onApplyPreset(previewedPresetId, pagesToApply);
                                }

                                // Clear preview filters
                                if (setPreviewFilters && setPreviewPages) {
                                  setPreviewFilters(null);
                                  setPreviewPages(new Set());
                                }

                                // Finalize the preset application
                                setManualModeChange(true);
                                setModeWasAutoSelected(false);
                                setPageSettingMode('preset');
                                setOriginalPageExceptions(null); // Finalize changes
                                setPreviewedPresetId(null);
                                setBottomSheetOpen(false);
                              }}
                              className="flex-1 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors"
                            >
                              <div className="leading-tight">
                                {allPagesSelected ? (<>Apply to<br />Selected (All)</>) : (<>Apply to<br />Selected</>)}
                              </div>
                            </button>
                            {/* Create New Preset Button - only if there are actual adjustments in the previewed preset */}
                            {previewedPresetId &&
                              savedPresets.find(p => p.id === previewedPresetId) &&
                              Object.values(savedPresets.find(p => p.id === previewedPresetId).settings).some(val =>
                                (typeof val === 'boolean' && val === true) || (typeof val === 'number' && val !== 100) ||
                                (typeof val === 'number' && val !== 0.15) // Check for bgRemovalTolerance
                              ) && (
                                <button
                                  onClick={() => setCreatingNewPreset(true)}
                                  className="flex-1 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg text-xs font-semibold hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
                                >
                                  <div className="leading-tight">
                                    Create New<br />Preset
                                  </div>
                                </button>
                              )}
                          </div>
                        ) : (
                          // If there are remove buttons, or no preset is previewed yet
                          <>
                            {/* Remove Buttons - show if there were presets originally */}
                            {showRemoveButtons && (
                              <div className="border-2 border-red-200 dark:border-red-800 rounded-lg p-2 bg-red-50/50 dark:bg-red-900/20">
                                <p className="text-[10px] font-semibold text-red-700 dark:text-red-300 mb-1.5 px-1">Remove Preset:</p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      // Clear preview filters
                                      if (setPreviewFilters && setPreviewPages) {
                                        setPreviewFilters(null);
                                        setPreviewPages(new Set());
                                      }
                                      // Remove preset from selected pages
                                      if (onDeleteException) {
                                        const pagesToClear = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                          ? Array.from(selectedContactSheetPages)
                                          : [currentPageNumber];
                                        pagesToClear.forEach(pageNum => {
                                          const pageException = pageExceptions[pageNum];
                                          if (pageException?.enabled && pageException?.presetId) {
                                            onDeleteException(pageNum);
                                          }
                                        });
                                      }
                                      // Reset mode to allow auto-detection to run
                                      setManualModeChange(false);
                                      setPageSettingMode('');
                                      setBottomSheetOpen(false);
                                      setOriginalPageExceptions(null);
                                      setPreviewedPresetId(null);
                                    }}
                                    className="flex-1 py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
                                  >
                                    <div className="leading-tight">
                                      Remove from<br />Selected
                                    </div>
                                  </button>

                                  {allPagesWithPresetAreSelected ? (
                                    // If all pages with presets are currently selected, no "Remove from All" is needed
                                    null
                                  ) : (
                                    <button
                                      onClick={() => {
                                        // Clear preview filters
                                        if (setPreviewFilters && setPreviewPages) {
                                          setPreviewFilters(null);
                                          setPreviewPages(new Set());
                                        }
                                        // Remove preset from ALL pages that have it
                                        if (onDeleteException) {
                                          pagesWithPreset.forEach(pageNum => {
                                            onDeleteException(pageNum);
                                          });
                                        }
                                        // Reset mode to allow auto-detection to run
                                        setManualModeChange(false);
                                        setPageSettingMode('');
                                        setBottomSheetOpen(false);
                                        setOriginalPageExceptions(null);
                                        setPreviewedPresetId(null);
                                      }}
                                      className="flex-1 py-2 bg-red-700 dark:bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-800 dark:hover:bg-red-700 transition-colors"
                                    >
                                      <div className="leading-tight">
                                        Remove from<br />All Pages
                                      </div>
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Apply Button */}
                            {previewedPresetId && (
                              <button
                                onClick={() => {
                                  // Apply the preset to the selected pages
                                  if (previewedPresetId && onApplyPreset) {
                                    const pagesToApply = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                                      ? Array.from(selectedContactSheetPages)
                                      : [currentPageNumber];
                                    onApplyPreset(previewedPresetId, pagesToApply);
                                  }

                                  // Clear preview filters
                                  if (setPreviewFilters && setPreviewPages) {
                                    setPreviewFilters(null);
                                    setPreviewPages(new Set());
                                  }

                                  // Finalize the preset application
                                  setManualModeChange(true);
                                  setModeWasAutoSelected(false);
                                  setPageSettingMode('preset');
                                  setOriginalPageExceptions(null); // Finalize changes
                                  setPreviewedPresetId(null);
                                  setBottomSheetOpen(false);
                                }}
                                className="w-full py-3 bg-purple-600 dark:bg-purple-500 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors"
                              >
                                <div className="leading-tight">
                                  {allPagesSelected ? (<>Apply to<br />Selected (All)</>) : (<>Apply to<br />Selected</>)}
                                </div>
                              </button>
                            )}

                            {/* Create New Preset Button - (standalone, or next to apply button if no remove buttons) */}
                            {!showRemoveButtons && (
                              <button
                                onClick={() => setCreatingNewPreset(true)}
                                className="w-full py-3 bg-green-600 dark:bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
                              >
                                Create New Preset
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {bottomSheetMode === 'preset' && creatingNewPreset && (
                <div className="space-y-4">
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Enter a name for your new preset:
                  </p>
                  <input
                    type="text"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={newPresetName}
                    onChange={(e) => setNewPresetName(e.target.value)}
                    placeholder="My New Preset"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCreatingNewPreset(false)}
                      className="flex-1 py-2 bg-gray-500 dark:bg-gray-600 text-white rounded-lg text-sm font-semibold hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (newPresetName.trim() !== '') {
                          // Find the actual preset being previewed
                          const presetToSave = savedPresets.find(p => p.id === previewedPresetId);
                          if (presetToSave) {
                            onSavePreset(newPresetName, presetToSave.settings);
                            setCreatingNewPreset(false);
                            setNewPresetName('');
                            setBottomSheetOpen(false); // Close bottom sheet after saving
                          } else {
                            // If no preset was previewed, use the current active filters
                            onSavePreset(newPresetName, filters);
                            setCreatingNewPreset(false);
                            setNewPresetName('');
                            setBottomSheetOpen(false); // Close bottom sheet after saving
                          }
                        }
                      }}
                      disabled={newPresetName.trim() === ''}
                      className="flex-1 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-700 dark:hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Save Preset
                    </button>
                  </div>
                </div>
              )}

              {bottomSheetMode === 'custom' && (
                <div className="space-y-3">
                  {/* Toggles Row */}
                  <div className="grid grid-cols-3 gap-3">
                    {/* Invert */}
                    <div>
                      <label className="text-xs font-medium text-gray-800 dark:text-gray-100 block mb-1">Invert</label>
                      <button
                        onClick={() => handleFilterChange('invert', !previewFilters.invert)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          previewFilters.invert ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          previewFilters.invert ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {/* Grayscale */}
                    <div>
                      <label className="text-xs font-medium text-gray-800 dark:text-gray-100 block mb-1">Grayscale</label>
                      <button
                        onClick={() => handleFilterChange('grayscale', !previewFilters.grayscale)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          previewFilters.grayscale ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          previewFilters.grayscale ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {/* Remove BG */}
                    <div>
                      <label className="text-xs font-medium text-gray-800 dark:text-gray-100 block mb-1">Remove BG</label>
                      <button
                        onClick={() => handleFilterChange('removeBackground', !previewFilters.removeBackground)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          previewFilters.removeBackground ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          previewFilters.removeBackground ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  </div>

                  {/* Sliders - 2 Columns */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Brightness */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-medium text-gray-800 dark:text-gray-100">Brightness</label>
                        <span className="text-xs text-gray-600 dark:text-gray-300">{previewFilters.brightness}%</span>
                      </div>
                      <input
                        type="range"
                        min="50"
                        max="200"
                        value={previewFilters.brightness}
                        onChange={(e) => setPreviewFilters({ ...previewFilters, brightness: Number(e.target.value) })}
                        className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                    </div>

                    {/* Contrast */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-medium text-gray-800 dark:text-gray-100">Contrast</label>
                        <span className="text-xs text-gray-600 dark:text-gray-300">{previewFilters.contrast}%</span>
                      </div>
                      <input
                        type="range"
                        min="50"
                        max="200"
                        value={previewFilters.contrast}
                        onChange={(e) => setPreviewFilters({ ...previewFilters, contrast: Number(e.target.value) })}
                        className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => {
                        // Clear preview filters
                        if (setPreviewFilters && setPreviewPages) {
                          setPreviewFilters(null);
                          setPreviewPages(new Set());
                        }

                        // Restore original filters (if any)
                        if (originalCustomFilters) {
                          onFilterChange('all', originalCustomFilters); // Apply all original filters
                        }

                        // Clear mode and original filters
                        setPageSettingMode('');
                        setManualModeChange(false);
                        setOriginalCustomFilters(null);
                        setBottomSheetOpen(false); // Close bottom sheet
                      }}
                      className="flex-1 py-2 bg-gray-500 dark:bg-gray-600 text-white rounded-lg text-sm font-semibold hover:bg-gray-600 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (onApplyToCurrentPage) {
                          const pagesToApply = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                            ? Array.from(selectedContactSheetPages)
                            : [currentPageNumber];

                          pagesToApply.forEach(pageNum => {
                            onApplyToCurrentPage(pageNum, previewFilters);
                          });
                        }
                        setBottomSheetOpen(false); // Close bottom sheet
                        setManualModeChange(true); // Indicate manual change
                        setPageSettingMode('custom'); // Set mode to custom
                        setOriginalCustomFilters(null); // Clear original filters
                      }}
                      className="flex-1 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors"
                    >
                      Apply Custom
                    </button>
                  </div>
                </div>
              )}

              {bottomSheetMode === 'auto-optimize' && (
                <div className="space-y-4">
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                      ? `Analyze ${selectedContactSheetPages.size} selected page${selectedContactSheetPages.size !== 1 ? 's' : ''} to suggest the best settings for minimizing ink usage.`
                      : 'Analyze the current page to suggest the best settings for minimizing ink usage.'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    This feature intelligently adjusts brightness, contrast, and removes backgrounds to reduce ink consumption while maintaining readability.
                  </p>
                  <button
                    onClick={() => {
                      if (onAutoOptimize) {
                        const pagesToAnalyze = currentViewMode === 'contactSheet' && selectedContactSheetPages.size > 0
                          ? Array.from(selectedContactSheetPages)
                          : [currentPageNumber];
                        onAutoOptimize(pagesToAnalyze);
                      }
                      setBottomSheetOpen(false);
                    }}
                    className="w-full py-3 bg-purple-600 dark:bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors"
                  >
                    Run Auto-Optimize
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PDFSidebar;
